import { Message, MediaItem } from 'src/services/whatsapp'
import { format } from 'date-fns'
import { cn } from 'src/utils/style'
import { ArrowDownTrayIcon, DocumentIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

interface MessageBubbleProps {
  message: Message
}

function MediaAttachment({ item, isInbound }: { item: MediaItem; isInbound: boolean }) {
  const { t } = useTranslation()
  const isImage = item.contentType.startsWith('image/')

  if (isImage) {
    return (
      <a href={item.url} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={item.url}
          alt={item.filename || t('chat.mediaAttachment')}
          className="max-h-48 max-w-full rounded-md object-contain"
          loading="lazy"
        />
      </a>
    )
  }

  // Non-image: show download link
  return (
    <a
      href={item.url}
      download={item.filename || true}
      target="_blank"
      rel="noopener noreferrer"
      className={cn([
        'flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-opacity hover:opacity-80',
        isInbound
          ? 'border-border-strong bg-surface text-gray-700'
          : 'border-primary-300 bg-primary-600 text-white',
      ])}
    >
      <DocumentIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="truncate">{item.filename || t('chat.downloadFile')}</span>
      <ArrowDownTrayIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
    </a>
  )
}

export const MessageBubble = ({ message }: MessageBubbleProps) => {
  const { t } = useTranslation()
  const isInbound = message.direction === 'inbound'
  const isOutbound = message.direction === 'outbound'
  const hasMedia = message.media && message.media.length > 0

  const formatTime = (dateString: string | null | undefined) => {
    if (!dateString) return ''
    try {
      return format(new Date(dateString), 'HH:mm')
    } catch {
      return ''
    }
  }

  return (
    <div className={cn(['flex w-full', isInbound ? 'justify-start' : 'justify-end'])}>
      <div
        className={cn([
          'max-w-[70%] rounded-lg px-4 py-2',
          isInbound
            ? 'rounded-bl-none bg-gray-200 text-text-primary'
            : 'rounded-br-none bg-primary-500 text-white',
        ])}
      >
        {/* Media attachments */}
        {hasMedia && (
          <div className="mb-2 flex flex-col gap-2">
            {message.media!.map((item, idx) => (
              <MediaAttachment key={idx} item={item} isInbound={isInbound} />
            ))}
          </div>
        )}

        {/* Text content */}
        {message.content && <p className="whitespace-pre-wrap break-words">{message.content}</p>}

        <div
          className={cn([
            'mt-1 flex items-center gap-1 text-xs',
            isInbound ? 'text-text-placeholder' : 'text-primary-100',
          ])}
        >
          <span>{formatTime(message.created)}</span>
          {isOutbound && message.sentByUser && (
            <span className="ml-1">
              • {t('chat.sentBy')} {message.sentByUser.firstName}
            </span>
          )}
        </div>
        {message.seenByUsers && message.seenByUsers.length > 0 && (
          <div className="mt-1 text-xs text-text-tertiary">
            {t('chat.seenBy')} {message.seenByUsers.map((u) => u.firstName).join(', ')}
          </div>
        )}
      </div>
    </div>
  )
}
